import { Add, Remove } from '@mui/icons-material';
import { Grid, Hidden } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';
import COLORS from '../../colors';
import { DURATION_SECONDS } from '../../constants';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { getMonthsBetweenDates, roundTwoDecimals } from '../../util';
import mapBurstToNumber from '../BudgetSplitSection/mapBurstToNumber';
import { Button } from '../Button';
import { SplitWeek } from '../SplitWeek';
import marshalData, { FormattedMonth } from './marshalData';

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledBorder = styled.div`
  background-color: ${COLORS.PRIMARY_500};
  height: 1px;
  width: 1500px;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledProductHeadingSpacer = styled.h5`
  width: 328px;
  flex-shrink: 0;
`;

const StyledProductHeading = styled.h5`
  font-size: 23.4px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.468px;
  color: ${COLORS.NEUTRAL_200};
  width: 264px;
  margin: 0 32px 0 0;
  padding: 16px 0;
  text-align: center;
  background-color: ${COLORS.NEUTRAL_925};
  border-radius: 12px 12px 0px 0px;
  flex-shrink: 0;
`;

const StyledColumnWeekGroup = styled.div`
  display: flex;
  flex-direction: row;
  margin-right: 32px;
`;

const StyledColumnHeadingGroup = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${COLORS.NEUTRAL_925};
  margin-right: 32px;
`;

const StyledColumnHeadingGroupEnd = styled.div`
  display: flex;
  flex-direction: row;
  margin-right: 32px;
`;

const StyledColumnHeading = styled.p`
  font-size: 13.33px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: ${COLORS.NEUTRAL_500};
  width: 88px;
  padding: 8px 0;
  text-align: center;
  flex-shrink: 0;
`;

const StyledColumnHeadingEnd = styled.p`
  font-size: 13.33px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: ${COLORS.NEUTRAL_500};
  width: 96px;
  padding: 8px 0;
  text-align: center;
  flex-shrink: 0;
`;

const StyledMonth = styled.p`
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  color: ${COLORS.PRIMARY_500};
  width: 88px;
  padding: 12px 0;
  flex-shrink: 0;
`;

const StyledTotal = styled.p`
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  color: ${COLORS.PRIMARY_500};
  width: 96px;
  padding: 12px 0;
  flex-shrink: 0;
`;

const StyledWeek = styled.p`
  text-align: center;
  font-size: 13.33px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;
  color: ${COLORS.NEUTRAL_500};
  width: 88px;
  padding: 16px 0;
  margin-right: 32px;
  flex-shrink: 0;
`;

const StyledValueWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-right: 32px;
`;

const StyledValue = styled.p`
  text-align: center;
  font-size: 13.33px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: ${COLORS.NEUTRAL_200};
  width: 88px;
  padding: 16px 0;
  background-color: ${COLORS.NEUTRAL_925};
  flex-shrink: 0;
`;

const StyledGradient = styled.div`
  display: flex;
  width: 1222px;
  height: 128px;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: 0;
  background: linear-gradient(
    0deg,
    ${COLORS.NEUTRAL_950} 62%,
    rgba(8, 8, 12, 0) 100%
  );
`;

const CampaignSummary = forwardRef((props, ref) => {
  const { campaignDurations } = useAppSelector(
    (state: RootState) => state.detail
  );
  const { burst } = useAppSelector((state: RootState) => state);
  const { startDate, endDate } = useAppSelector(
    (state: RootState) => state.detail.campaignLength
  );
  const [data, setData] = useState<any[]>([]);
  const [weekCount, setWeekCount] = useState<number>(0);

  const formatData = () => {
    if (!startDate || !endDate) {
      return;
    }
    const months = getMonthsBetweenDates(startDate, endDate);

    const marshaledData = marshalData(burst, months);

    setWeekCount(getNumberOfWeeks(marshaledData));
    setData(marshaledData);
  };

  const getNumberOfWeeks = (data: FormattedMonth[]): number => {
    let count = 0;
    data.map((month) => {
      month.weeks.map((week) => {
        count++;
      });
    });

    return count;
  };

  useEffect(() => {
    formatData();
  }, [burst]);

  const [showExtraSection, setShowExtraSection] = useState<boolean>(false);
  const [showExtraSectionForPdf, setShowExtraSectionForPdf] =
    useState<boolean>(false);

  const toggleExtraSection = () => {
    setShowExtraSection(!showExtraSection);
  };

  useImperativeHandle(ref, () => ({
    showExtraSection() {
      setShowExtraSection(true);
      setShowExtraSectionForPdf(true);
    },
    hideExtraSection() {
      setShowExtraSection(false);
      setShowExtraSectionForPdf(false);
    }
  }));

  return (
    <>
      {data && (
        <>
          <Grid
            sx={
              showExtraSection
                ? {
                    width: '1222px',
                    height: 'auto',
                    overflowX: 'scroll',
                    overflowY: 'hidden'
                  }
                : weekCount > 7
                  ? {
                      width: '1222px',
                      height: '500px',
                      overflowX: 'scroll',
                      overflowY: 'hidden'
                    }
                  : {
                      width: '1222px',
                      height: 'auto',
                      paddingBottom: '60px',
                      overflowX: 'scroll'
                    }
            }
            item
            xs={6}
          >
            <StyledColumn>
              <StyledRow>
                <StyledProductHeadingSpacer>&nbsp;</StyledProductHeadingSpacer>
                {Object.entries(campaignDurations).map(
                  ([duration, isSelected]) => {
                    if (!isSelected) {
                      return;
                    }

                    const durationNumber = mapBurstToNumber(
                      duration as DURATION_SECONDS
                    );
                    return (
                      <StyledProductHeading key={durationNumber}>
                        {durationNumber}" Burst
                      </StyledProductHeading>
                    );
                  }
                )}
              </StyledRow>
            </StyledColumn>
            <StyledColumn>
              <StyledRow>
                <StyledColumnHeading>&nbsp;</StyledColumnHeading>
                <StyledColumnWeekGroup>
                  <StyledColumnHeading>W/C</StyledColumnHeading>
                </StyledColumnWeekGroup>
                <StyledColumnWeekGroup>
                  <StyledColumnHeading>Split</StyledColumnHeading>
                </StyledColumnWeekGroup>
                {Object.entries(campaignDurations).map(([_, isSelected]) => {
                  if (!isSelected) {
                    return;
                  }

                  return (
                    <StyledColumnHeadingGroup>
                      <StyledColumnHeading>CPT</StyledColumnHeading>
                      <StyledColumnHeading>TARPS</StyledColumnHeading>
                      <StyledColumnHeading>COST</StyledColumnHeading>
                    </StyledColumnHeadingGroup>
                  );
                })}
                <StyledColumnHeadingGroupEnd>
                  <StyledColumnHeadingEnd>
                    Monthly Budget
                  </StyledColumnHeadingEnd>
                </StyledColumnHeadingGroupEnd>
              </StyledRow>
            </StyledColumn>
            {data.map((item: any) => (
              <StyledColumn key={item.id}>
                {item.weeks.map((week: any, i: any) => (
                  <>
                    {i === 0 && <StyledBorder />}
                    <StyledRow>
                      {/* Month */}
                      <StyledMonth>{i === 0 ? item.month : ''}</StyledMonth>

                      {/* W/C */}
                      <StyledWeek key={week.weekCommencing}>
                        {week.weekCommencing}
                      </StyledWeek>

                      {/* Split */}
                      <StyledWeek key={week.weekCommencing + week.isSplit}>
                        {week.isSplit && (
                          <SplitWeek
                            isSplit={true}
                            direction={week.direction}
                            numberOfDays={week.numberOfDays}
                          />
                        )}
                      </StyledWeek>

                      {/* CPT/Tarps/Cost */}
                      {Object.values(DURATION_SECONDS).map((duration) => {
                        if (!week[duration]) {
                          return;
                        }

                        return week[duration].map((item: any) => (
                          <StyledValueWrapper>
                            <StyledValue key={item.cpt}>
                              $
                              {roundTwoDecimals(item.cpt).toLocaleString(
                                undefined,
                                { minimumFractionDigits: 2 }
                              )}
                            </StyledValue>
                            <StyledValue key={item.tarps}>
                              {roundTwoDecimals(item.tarps).toLocaleString(
                                undefined,
                                { minimumFractionDigits: 2 }
                              )}
                            </StyledValue>
                            <StyledValue key={item.cost}>
                              $
                              {roundTwoDecimals(item.cost).toLocaleString(
                                undefined,
                                { minimumFractionDigits: 2 }
                              )}
                            </StyledValue>
                          </StyledValueWrapper>
                        ));
                      })}

                      <StyledTotal>
                        {i === item.weeks.length - 1
                          ? `$${Number(item.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                          : ''}
                      </StyledTotal>
                    </StyledRow>
                  </>
                ))}
              </StyledColumn>
            ))}
          </Grid>
          <Grid
            sx={{
              display: 'flex',
              position: 'relative',
              justifyContent: 'center',
              marginTop: '24px'
            }}
            item
            xs={6}
          >
            {!showExtraSection && <StyledGradient />}
            {weekCount >= 7 && (
              <Button
                icon={showExtraSection ? <Remove /> : <Add />}
                label={showExtraSection ? 'Show less' : 'Show more'}
                backgroundColor="transparent"
                hoverBackgroundColor={COLORS.BUTTON_TERTIARY_HOVER}
                textColor={COLORS.PRIMARY_500}
                showShadow={false}
                borderColor={COLORS.NEUTRAL_700}
                onClick={toggleExtraSection}
              />
            )}
          </Grid>
        </>
      )}
    </>
  );
});

export default CampaignSummary;
